import React, { useState, useEffect } from 'react';
import LanguageSelector from '../Button/LanguageSelector';
import OptimizedLogo from '../OptimizedImage/OptimizedLogo';
import { usePublicTranslation } from '../../../hooks/usePublicTranslation';
import { getSolutionsData, getAboutLinks } from '../../../content/public/navigationData';

interface NavbarProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
  translations?: any;
}

const Navbar: React.FC<NavbarProps> = ({ currentLanguage, onLanguageChange }) => {
  const { t } = usePublicTranslation();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solutionsData = getSolutionsData(t);
  const aboutLinks = getAboutLinks(t);

  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'home') {
      const currentPath = window.location.pathname.replace(/^\//, '');
      if (currentPath === '' || currentPath === 'index.html') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        // If element not found (e.g. not on home), go home first
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const handleLinkClick = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    setAboutDropdownOpen(false);
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-[1000] transition-all duration-300 bg-[linear-gradient(135deg,#1c1e22_0%,#0d5c55_100%)] shadow-[0_4px_30px_rgba(0,0,0,0.15),0_1px_0_rgba(255,255,255,0.1)_inset] border-b border-white/15 hover:shadow-[0_6px_40px_rgba(0,0,0,0.2),0_1px_0_rgba(255,255,255,0.15)_inset] hover:border-white/20" style={{ backdropFilter: 'blur(15px) saturate(180%)' }}>
      <div className="container mx-auto px-4 sm:px-6 md:px-10 flex justify-between items-center relative h-[70px] md:h-[80px]">

        {/* Logo */}
        <div className="cursor-pointer z-50 flex items-center" onClick={() => scrollToSection('home')}>
          <OptimizedLogo
            name="logonav"
            alt="NexuCapital"
            className={`w-auto transition-all duration-300 ${scrolled ? 'h-[70px] md:h-[100px]' : 'h-[90px] md:h-[140px] max-h-[250px]'}`}
            loading="eager"
          />
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8">
          <button onClick={() => scrollToSection('home')} className={`text-base font-medium text-white hover:text-[#3498db] hover:bg-white/10 px-4 py-2 rounded-md transition-all`}>
            {t.navigation.home}
          </button>

          {/* Solutions Dropdown */}
          <div className="relative group/dropdown"> {/* Renamed group to group/dropdown to avoid conflicts */}
            <button
              className={`flex items-center text-base font-medium text-white hover:text-[#3498db] hover:bg-white/10 px-4 py-2 rounded-md transition-all`}
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              {t.navigation.solutions}
              <span className="ml-1 text-xs transition-transform group-hover/dropdown:rotate-180">▼</span>
            </button>

            <div
              className={`absolute top-full -left-[50px] w-[380px] pt-4 z-[1001] transition-all duration-300 ${dropdownOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <div className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.15)] rounded-xl overflow-hidden border border-black/10">
                <div className="flex">
                  <div className="bg-[#f8f9fa] p-6 flex-1">
                    <h3 className="text-[#2c3e50] font-bold mb-4 uppercase text-sm tracking-wider">{t.navigation.ourSolutions}</h3>
                    {solutionsData.map((s: any) => (
                      <div key={s.id} onClick={() => scrollToSection('solutions')} className="mb-2 solution-item cursor-pointer hover:bg-[rgba(52,152,219,0.08)] p-3 rounded-md transition-colors group/item">
                        <div className="font-semibold text-[#2c3e50] text-[0.95rem] block mb-1">{s.name}</div>
                        <p className="text-[0.8rem] text-[#666] leading-relaxed m-0">{s.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button onClick={() => scrollToSection('compliance')} className={`text-base font-medium text-white hover:text-[#3498db] hover:bg-white/10 px-4 py-2 rounded-md transition-all`}>
            {t.navigation.compliance}
          </button>

          {/* About Dropdown */}
          <div className="relative group/about">
            <button
              className={`flex items-center text-base font-medium text-white hover:text-[#3498db] hover:bg-white/10 px-4 py-2 rounded-md transition-all`}
              onMouseEnter={() => setAboutDropdownOpen(true)}
              onMouseLeave={() => setAboutDropdownOpen(false)}
            >
              {t.navigation.about}
              <span className="ml-1 text-xs transition-transform group-hover/about:rotate-180">▼</span>
            </button>
            <div
              className={`absolute top-full -left-[50px] min-w-[280px] pt-4 z-[1001] transition-all duration-300 ${aboutDropdownOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'}`}
              onMouseEnter={() => setAboutDropdownOpen(true)}
              onMouseLeave={() => setAboutDropdownOpen(false)}
            >
              <div className="bg-[#f8f9fa] shadow-[0_10px_40px_rgba(0,0,0,0.15)] rounded-xl overflow-hidden border border-black/10">
                <div className="p-6 bg-[#f8f9fa] w-full">
                  {aboutLinks.map((link: any) => (
                    <div
                      key={link.id}
                      onClick={() => handleLinkClick(link.path)}
                      className="solution-item cursor-pointer hover:bg-[rgba(52,152,219,0.08)] p-3 rounded-md transition-colors mb-1"
                    >
                      <span className="font-semibold text-[#2c3e50] text-[0.9rem]">{link.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Press Link */}
          <button onClick={() => handleLinkClick('/press')} className={`text-base font-medium text-white hover:text-[#3498db] hover:bg-white/10 px-4 py-2 rounded-md transition-all`}>
            {t.navigation.press}
          </button>
        </div>

        {/* Actions - nav-actions */}
        <div className="hidden md:flex items-center gap-[1.5rem]">
          <div className="nav-language-right">
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={onLanguageChange}
            />
          </div>
          <a
            href="https://consulting.nexucapital.com.br/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[linear-gradient(135deg,#c9a227,#b8941f)] hover:bg-[linear-gradient(135deg,#b8941f,#c9a227)] text-white px-[1.25rem] py-[0.5rem] h-[36px] rounded-lg text-[0.85rem] font-medium transition-all transform hover:-translate-y-[2px] shadow-[0_2px_8px_rgba(201,162,39,0.3)] hover:shadow-[0_4px_15px_rgba(201,162,39,0.4)] flex items-center gap-2 whitespace-nowrap overflow-hidden relative"
          >
            <span className="text-[1.1rem] drop-shadow-[0_1px_2px_rgba(0,0,0,0.1)]">🔒</span>
            {t.navigation.clientArea || "Área Logada"}
          </a>
        </div>

        {/* Mobile: Language Selector + Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSelector
            currentLanguage={currentLanguage}
            onLanguageChange={onLanguageChange}
          />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`text-2xl focus:outline-none text-white p-2`}>
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-[linear-gradient(135deg,#1c1e22_0%,#0d5c55_100%)] shadow-2xl border-t border-white/10 text-white animate-fade-in-up">
          <div className="flex flex-col p-6 space-y-6 max-h-[80vh] overflow-y-auto">
            <button onClick={() => scrollToSection('home')} className="text-left font-medium text-lg text-white hover:text-secondary">
              {t.navigation.home}
            </button>

            <div>
              <div className="font-bold text-secondary mb-3 uppercase text-xs tracking-wider">{t.navigation.solutions}</div>
              {solutionsData.map((s: any) => (
                <button key={s.id} onClick={() => scrollToSection('solutions')} className="block w-full text-left text-base text-gray-200 mb-3 pl-4 border-l-2 border-white/10 hover:border-secondary hover:text-white transition-colors">
                  {s.name}
                </button>
              ))}
            </div>

            <div>
              <div className="font-bold text-secondary mb-3 uppercase text-xs tracking-wider">{t.navigation.about}</div>
              {aboutLinks.map((link: any) => (
                <button key={link.id} onClick={() => handleLinkClick(link.path)} className="block w-full text-left text-base text-gray-200 mb-3 pl-4 border-l-2 border-white/10 hover:border-secondary hover:text-white transition-colors">
                  {link.name}
                </button>
              ))}
            </div>

            {/* Press Link - Mobile */}
            <button onClick={() => handleLinkClick('/press')} className="text-left font-medium text-lg text-white hover:text-secondary">
              {t.navigation.press}
            </button>

            {/* Language Selector - Mobile */}
            <div className="flex items-center justify-center gap-4 py-4 border-t border-white/10">
              <span className="text-gray-400 text-sm">{t.navigation.language || 'Idioma'}:</span>
              <LanguageSelector
                currentLanguage={currentLanguage}
                onLanguageChange={(lang) => {
                  onLanguageChange(lang);
                  setMobileMenuOpen(false);
                }}
              />
            </div>

            <div className="pt-4 border-t border-white/10">
              <a
                href="https://consulting.nexucapital.com.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[linear-gradient(135deg,#c9a227,#b8941f)] text-white px-6 py-3 rounded-lg text-base font-bold shadow-lg flex items-center justify-center gap-2"
              >
                <span>🔒</span>
                {t.navigation.clientArea || "Área Logada"}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
